import React from "react";
import clsx from "clsx";
import makeStyles from "@mui/styles/makeStyles";
import { Fade, Typography, useMediaQuery, useTheme } from "@mui/material";

import CONFIG from "@APP/config";
import { MOBILE_FONT, MOBILE_GUTTER } from "@APP/styles/mobile";

import Header from "./Header";
import Footer, { FooterVariant, PAGE_CONTENT_ID } from "./Footer";
import SpokeSpinner from "./SpokeSpinner";

// The LBG host embeds this app in an iframe and sizes that iframe itself, so the
// host page owns the scrollbar. Matching the MTD ITSA widget: when embedded, the
// internal scroll container is set to `hidden` to avoid a redundant inner
// scrollbar (and its flicker); standalone the app must scroll itself (`auto`).
// The value is fixed for the app's lifetime, so it is read once at module load.
// (Comparing the window references is safe cross-origin — only reading the
// parent's properties would throw.)
const IS_IN_IFRAME = typeof window !== "undefined" && window.self !== window.top;

interface Props {
  children: React.ReactElement;
  loading?: boolean;
  loaderText?: string;
  /**
   * Which LBG footer bar to render — the journey screens use the default link
   * bar, the terminal / status screens use the "minimal" one. See Footer.tsx.
   */
  footerVariant?: FooterVariant;
  /**
   * Terminal screens such as the payment success state drop the header bar
   * entirely in the LBG designs.
   */
  hideHeader?: boolean;
  /**
   * Screens whose design shows no footer bar at all (e.g. the decline
   * confirmation journey) drop it entirely rather than picking a variant.
   */
  hideFooter?: boolean;
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
    width: "100%",
  },
  wrapper: {
    display: "flex",
    flex: "1 1 auto",
    overflow: "hidden",
  },
  contentContainer: {
    position: "relative",
    display: "flex",
    flex: "1 1 auto",
    overflow: "hidden",
  },
  content: {
    display: "flex",
    flexDirection: "column",
    flex: "1 1 auto",
    height: "100%",
    // Host page owns the scroll when embedded; scroll ourselves standalone.
    overflow: IS_IN_IFRAME ? "hidden" : "auto",
    backgroundColor: theme.palette.background.default,
  },
  mainContent: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    // The gutter lives here rather than on the scroll container so the footer
    // stays full-bleed. 32px top spacing per the Figma; bottom kept modest so
    // the page fits the viewport without a scrollbar on typical desktop heights.
    padding: "32px 5vw 24px",
    [theme.breakpoints.down("sm")]: {
      // 32px header-to-title gap per the mobile Figma; the mobile footer is a
      // short centred mark rather than a link bar, so no space is reserved for it.
      padding: `32px ${MOBILE_GUTTER}px 24px`,
    },
  },
  // Without the header bar there is nothing anchoring the content to the top of
  // the page, so the status screens that drop it (payment successful, link
  // unavailable, request declined) centre their block in the remaining space
  // instead of sitting where the header used to be. Vertical padding is kept
  // symmetric so the block lands optically centred.
  mainContentCentered: {
    paddingTop: 24,
    paddingBottom: 24,
    [theme.breakpoints.down("sm")]: {
      padding: `24px ${MOBILE_GUTTER}px`,
    },
  },
  // `margin: auto` rather than `justifyContent: center` on the flex parent:
  // auto margins collapse to 0 once the content is taller than the space, so a
  // long card still scrolls from its top instead of being clipped.
  centeredBlock: {
    margin: "auto",
    width: "100%",
  },
  loaderContainer: {
    position: "absolute",
    display: "flex",
    paddingTop: "20vh",
    paddingLeft: MOBILE_GUTTER,
    paddingRight: MOBILE_GUTTER,
    justifyContent: "center",
    // Transparent so the header (now inside the scroll flow) stays visible
    // during loading states like "Payment sending"; the main content behind is
    // already faded to opacity 0, so nothing else shows through.
    backgroundColor: "transparent",
    zIndex: 100,
    flexDirection: "column",
    width: "100%",
    alignItems: "center",
    gap: "16px",
    [theme.breakpoints.down("sm")]: {
      // On mobile the loader is the only thing on screen, so it sits optically
      // centred in the viewport rather than at a fixed offset from the top:
      // stretch the overlay over the whole content area and centre in it.
      // (`20vh` / a fixed offset both left it stranded in the upper third.)
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
      paddingTop: 0,
      justifyContent: "center",
    },
  },
  fade: { display: "flex", alignItems: "center", justifyContent: "center" },
  versionLabel: {
    position: "absolute",
    bottom: 5,
    right: 12,
    [theme.breakpoints.down("sm")]: {
      // On mobile this sits on the grey page background rather than the black
      // footer bar, where `color="primary"` black would be obtrusive.
      color: theme.palette.info.main,
      fontSize: MOBILE_FONT.caption,
    },
  },
}));

const PageLayout = ({
  children,
  loading = false,
  loaderText = "",
  footerVariant = "links",
  hideHeader = false,
  hideFooter = false,
}: Props) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <div className={classes.root} id="pageLayoutDiv">
      <div className={classes.wrapper} id="pageLayoutWrapper">
        <div className={classes.contentContainer} id="pageLayoutContainer">
          {loading && (
            <div className={classes.loaderContainer}>
              {/* The design shows just the headline and spinner — no
                  illustration — for the "Payment sending" state. */}
              {loaderText ? (
                // h4 rather than h6, matching the other status titles
                // (Message, RTPCompletePaymentScreen success) at the same
                // headline weight rather than MUI's much smaller h6 default.
                <Typography
                  component="p"
                  variant="h4"
                  sx={isMobile ? { fontSize: "24px", textAlign: "center" } : { fontSize: "30px" }}
                  id="rtpCompleteLoaderText">
                  {loaderText}
                </Typography>
              ) : null}
              <SpokeSpinner size={isMobile ? 40 : 56} />
            </div>
          )}
          {/* The header lives inside the scroll container so it flows with the
              page (not pinned/sticky) — the whole page scrolls as one on short
              viewports rather than the header staying put over a scrolling
              body. */}
          <div className={classes.content} id={PAGE_CONTENT_ID}>
            {!hideHeader && <Header />}
            <main
              className={clsx(classes.mainContent, { [classes.mainContentCentered]: hideHeader })}
              id="pageLayoutMainContent">
              <Fade timeout={700} in={!loading}>
                <div className={hideHeader ? classes.centeredBlock : undefined}>{children}</div>
              </Fade>
            </main>
            {!hideFooter && <Footer variant={footerVariant} />}
            <Typography
              variant="caption"
              color="primary"
              className={classes.versionLabel}
              id="versionNumber">{`v ${CONFIG.VERSION}`}</Typography>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageLayout;
